import usdFormatter from './usdFormatter';

/**
 * Builds the duration clause describing how long a coupon's discount lasts.
 *
 * Mirrors the wording of CheckoutScreen's discountCaption, but reads the
 * camelCase `durationInMonths` returned by the promo-preview endpoint (the
 * checkout screen consumes raw snake_case Stripe data instead).
 *
 * @param   {object}  coupon  Resolved coupon.
 * @returns {?string}         Duration sentence, or null when none applies.
 */
const durationSentence = (coupon) => {
    if ('forever' === coupon.duration) {
        return 'The discount lasts forever.';
    }

    if ('once' === coupon.duration) {
        return 'Applies to your first payment.';
    }

    if ('repeating' === coupon.duration) {
        return 1 < coupon.durationInMonths
            ? `Applies for the first ${coupon.durationInMonths} months.`
            : 'Applies for the first month.';
    }

    return null;
};

/**
 * Builds the "discount applied" sentence for the inbound-coupon notice shown
 * on the login/register screens.
 *
 * The message is split into a lead sentence and the duration clause so the
 * notice can render them on separate lines.
 *
 * @param   {object}  coupon                   Resolved coupon from the promo-preview endpoint.
 * @param   {?number} coupon.percentOff        Percent off (e.g. 50), or null.
 * @param   {?number} coupon.amountOff         Amount off in cents, or null.
 * @param   {?string} coupon.duration          Stripe duration: forever|once|repeating.
 * @param   {?number} coupon.durationInMonths  Months for a repeating duration.
 * @returns {?{lead: string, duration: ?string}} Notice parts, or null when no discount applies.
 */
export default (coupon) => {
    if (! coupon) {
        return null;
    }

    let amount;

    if (coupon.percentOff) {
        amount = `${coupon.percentOff}% off`;
    } else if (coupon.amountOff) {
        amount = `${usdFormatter(coupon.amountOff)} off`;
    } else {
        return null;
    }

    return {
        lead: `Your ${amount} discount has been applied - register to claim it.`,
        duration: durationSentence(coupon),
    };
};
